import type { RedirectRequest } from "@azure/msal-browser";
import { msalInstance } from "../msal/msalInstance";
import {
  hasPendingLogout,
  markLogoutPending,
  recordLoginAttempt,
} from "@/shared/helper/authSession.helper";
import {
  LOGOUT_SIGNAL_KEY,
  PRESERVED_KEY_PREFIXES,
} from "@/shared/constants/auth.constants";

const readLogoutSignal = (): string | null => {
  try {
    return localStorage.getItem(LOGOUT_SIGNAL_KEY);
  } catch {
    return null;
  }
};

// Last logout signal this tab has seen or written; a different stored value means another tab signed out.
let acknowledgedLogoutSignal = readLogoutSignal();

// After sign-out in another tab, this tab keeps its page until the user interacts with it.
let isLoginDeferredUntilUserAction = false;
window.addEventListener("storage", (event: StorageEvent) => {
  if (event.key === LOGOUT_SIGNAL_KEY && event.newValue) {
    isLoginDeferredUntilUserAction = true;
  }
});

// Reads shared storage directly because storage events and MSAL broadcasts can arrive in either order.
const deferLoginIfSignedOutElsewhere = (): boolean => {
  const logoutSignal = readLogoutSignal();
  if (logoutSignal !== null && logoutSignal !== acknowledgedLogoutSignal) {
    isLoginDeferredUntilUserAction = true;
  }
  return isLoginDeferredUntilUserAction;
};

export const isSignedOutInAnotherTab = (): boolean =>
  deferLoginIfSignedOutElsewhere() || hasPendingLogout();

export const resumeLoginAfterUserAction = (): void => {
  isLoginDeferredUntilUserAction = false;
  acknowledgedLogoutSignal = readLogoutSignal();
};

let isLoginRedirectInProgress = false;

/** Returns false when login is deferred or a redirect is already in progress. */
export const requestLoginRedirect = async (
  request: RedirectRequest,
): Promise<boolean> => {
  if (deferLoginIfSignedOutElsewhere() || isLoginRedirectInProgress) {
    return false;
  }
  isLoginRedirectInProgress = true;
  try {
    if (hasPendingLogout()) {
      const correlationId = request.correlationId ?? crypto.randomUUID();
      recordLoginAttempt(correlationId);
      await msalInstance.loginRedirect({ ...request, correlationId });
      return true;
    }
    await msalInstance.loginRedirect(request);
    return true;
  } catch (error) {
    isLoginRedirectInProgress = false;
    throw error;
  }
};

const attemptStorageOperation = (operation: () => void): void => {
  try {
    operation();
  } catch {
    return;
  }
};

/** Clears app storage and notifies other tabs. Call right before logoutRedirect. */
export const beginSignOut = (): void => {
  // Publish the signal before the pending marker so no tab sees one without the other.
  attemptStorageOperation(() => {
    const signal = String(Date.now());
    localStorage.setItem(LOGOUT_SIGNAL_KEY, signal);
    acknowledgedLogoutSignal = signal;
  });
  markLogoutPending();
  attemptStorageOperation(() => {
    Object.keys(localStorage).forEach((key) => {
      const lowerKey = key.toLowerCase();
      if (!PRESERVED_KEY_PREFIXES.some((prefix) => lowerKey.startsWith(prefix))) {
        localStorage.removeItem(key);
      }
    });
  });
};

export const onSignOutFromOtherTab = (callback: () => void): (() => void) => {
  const handleStorage = (event: StorageEvent): void => {
    if (event.key === LOGOUT_SIGNAL_KEY && event.newValue) {
      callback();
    }
  };
  window.addEventListener("storage", handleStorage);
  return () => window.removeEventListener("storage", handleStorage);
};
